/* ─────────────────────────────────────────────
 * Privacy Policy — Structured Content (IT / EN)
 * ───────────────────────────────────────────── */

export interface PolicySection {
  number: string;
  title: string;
  blocks: PolicyBlock[];
  subsections?: PolicySection[];
}

export type PolicyBlock =
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "table"; cols: string[]; rows: string[][] };

export interface PolicyData {
  title: string;
  subtitle: string;
  preambleTitle: string;
  preamble: string[];
  sections: PolicySection[];
  langToggle: { en: string; it: string };
  backLabel: string;
}

/* ── Italian ─────────────────────────────────── */

export const it: PolicyData = {
  title: "PRIVACY POLICY",
  subtitle: "art. 13 del Regolamento UE 679/2016",
  preambleTitle: "Premessa",
  langToggle: { en: "English", it: "Italiano" },
  backLabel: "Chiudi",
  preamble: [
    "Ai sensi del Regolamento Europeo n. 679/2016 (di seguito \"Regolamento\") e della normativa nazionale in materia di protezione dei dati personali (di seguito complessivamente \"Normativa Privacy\"), Hivly S.r.l. La invita a leggere con attenzione la presente Privacy Policy che contiene informazioni importanti sulla tutela dei Suoi Dati Personali.",
    "Il trattamento dei Suoi Dati Personali sarà improntato ai principi di correttezza, liceità, trasparenza, limitazione delle finalità e della conservazione, minimizzazione ed esattezza, integrità e riservatezza, in accordo con le disposizioni del Regolamento.",
  ],
  sections: [
    {
      number: "1",
      title: "TITOLARE e/o RESPONSABILE DEL TRATTAMENTO",
      blocks: [
        {
          kind: "p",
          text: "Titolare e/o Responsabile del trattamento dei Dati Personali è Hivly S.r.l. (di seguito \"Titolare\") con sede legale in Viale Lucania 35, Milano.",
        },
      ],
    },
    {
      number: "2",
      title: "DATI PERSONALI TRATTATI",
      blocks: [
        {
          kind: "ul",
          items: [
            "Dati anagrafici: nome e cognome;",
            "Dati di contatto: e-mail, indirizzo, numero di telefono;",
            "Dati di geo-localizzazione e navigazione: indirizzi IP raccolti tramite cookie tecnici (si rimanda alla Cookie Policy presente sul sito web);",
            "Dati bancari: necessari per la gestione del rapporto commerciale;",
            "Dati multimediali: immagini e riprese video raccolte durante eventuali eventi o promozioni;",
            "Dati da Social Network: eventuali like, commenti e contenuti condivisi sulle pagine Instagram e Linkedin di Hivly S.r.l.;",
          ],
        },
      ],
    },
    {
      number: "3",
      title: "FINALITÀ, BASI GIURIDICHE E CONSERVAZIONE",
      blocks: [
        {
          kind: "p",
          text: "Di seguito sono indicate le finalità del trattamento, le basi giuridiche utilizzate e i relativi tempi di conservazione:",
        },
        {
          kind: "table",
          cols: ["Finalità", "Base Giuridica", "Conservazione"],
          rows: [
            [
              "Esecuzione del servizio e Demo: erogazione del servizio secondo i Termini e Condizioni.",
              "Esecuzione di un contratto o misure precontrattuali (Art. 6.1.b GDPR).",
              "Per la durata del rapporto contrattuale e comunque non oltre 10 anni successivi alla cessazione.",
            ],
            [
              "Adempimenti fiscali: gestione della fatturazione e obblighi di legge.",
              "Obbligo legale (Art. 6.1.c GDPR).",
              "10 anni come previsto dalla normativa civile e fiscale.",
            ],
            [
              "Marketing e Newsletter: invio di comunicazioni commerciali e promozionali.",
              "Consenso dell'interessato (Art. 6.1.a GDPR).",
              "Fino alla revoca del consenso (o massimo 24 mesi).",
            ],
            [
              "Profilazione: analisi delle abitudini e scelte di consumo.",
              "Consenso specifico (Art. 6.1.a GDPR).",
              "12 mesi dalla raccolta, salvo revoca anticipata.",
            ],
            [
              "Analisi statistica: miglioramento dei servizi tramite dati anonimizzati.",
              "Legittimo interesse del Titolare (Art. 6.1.f GDPR).",
              "Trattamento in forma anonima; i dati non consentono l'identificazione.",
            ],
          ],
        },
      ],
    },
    {
      number: "4",
      title: "NATURA DEL CONFERIMENTO",
      blocks: [
        {
          kind: "p",
          text: "Il conferimento dei dati per finalità contrattuali e di fatturazione è necessario; il mancato conferimento impedisce l'erogazione del servizio. Per finalità di marketing e profilazione il conferimento è facoltativo.",
        },
        {
          kind: "p",
          text: "La natura obbligatoria o facoltativa del conferimento sarà di volta in volta segnalata mediante l'uso di simboli (ad es. \"*\") posti al fianco dell'informazione il cui conferimento è obbligatorio per perseguire la rispettiva finalità.",
        },
      ],
    },
    {
      number: "5",
      title: "MODALITÀ DEL TRATTAMENTO E DESTINATARI",
      blocks: [
        {
          kind: "p",
          text: "I dati sono trattati con strumenti elettronici e manuali garantendo la massima sicurezza. Possono essere accessibili a dipendenti qualificati di Hivly S.r.l. o soggetti terzi nominati Responsabili del trattamento. I dati non sono ceduti a terzi estranei alle attività di Hivly S.r.l..",
        },
        {
          kind: "p",
          text: "I dati personali saranno trattati in conformità alle prescrizioni della vigente Normativa Privacy, sia con l'ausilio di mezzi elettronici ed automatizzati sia con modalità manuali, con logiche strettamente correlate alle finalità del trattamento, mediante i data base, le piattaforme elettroniche gestite da Hivly S.r.l. o da terzi (nominati Responsabili del trattamento), i sistemi informatici integrati di Hivly S.r.l. e dei predetti terzi, e/o siti web di titolarità o in uso a Hivly S.r.l..",
        },
        {
          kind: "p",
          text: "I dati personali saranno trattati con modalità atte a garantirne la massima sicurezza e riservatezza e solo ad opera di persone istruite ed autorizzate al loro trattamento. Il Titolare adotta misure tecniche ed organizzative adeguate a garantire un livello di sicurezza adeguato al rischio dei trattamenti.",
        },
        {
          kind: "p",
          text: "I dati personali sono trattati principalmente presso le sedi del Titolare e nei luoghi in cui si trovano i Responsabili del trattamento.",
        },
      ],
    },
    {
      number: "6",
      title: "TRASFERIMENTO DATI EXTRA-UE",
      blocks: [
        {
          kind: "p",
          text: "Il trattamento avviene principalmente presso le sedi del Titolare nell'UE. In caso di trasferimento verso paesi extra-UE, il Titolare garantisce l'adozione di garanzie adeguate (come le Clausole Contrattuali Standard).",
        },
      ],
    },
    {
      number: "7",
      title: "DIRITTI DELL'INTERESSATO",
      blocks: [
        {
          kind: "p",
          text: "L'interessato può esercitare i diritti previsti dagli artt. 15 e ss. del GDPR:",
        },
        {
          kind: "ul",
          items: [
            "Accesso, rettifica, cancellazione e portabilità dei dati.",
            "Revoca del consenso e opposizione al trattamento.",
            "Diritto di Reclamo: È possibile proporre reclamo all'Autorità Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).",
          ],
        },
      ],
    },
    {
      number: "8",
      title: "SOCIAL NETWORK",
      blocks: [
        {
          kind: "p",
          text: "Oltre alle suindicate categorie di dati personali potranno essere trattati ulteriori dati dell'interessato direttamente conferiti (i c.d. \"Contributi\") e condivisi sulle pagine dei Social Network dedicate a Hivly S.r.l. (Instagram e Linkedin), per la cui gestione si rinvia alle terze parti che forniscono tali servizi. Fra i dati raccolti mediante i Social vi sono, ad esempio, i like, i commenti, le immagini ed in generale ogni contenuto ed informazione eventualmente dall'interessato pubblicata sulle pagine dei Social Network dedicate a Hivly S.r.l..",
        },
      ],
    },
    {
      number: "9",
      title: "MODALITÀ DI ESERCIZIO DEI DIRITTI",
      blocks: [
        {
          kind: "p",
          text: "Nell'eventualità Lei voglia esercitare i suddetti diritti, oppure voglia ricevere ulteriori chiarimenti in merito al trattamento dei suoi dati personali, può scrivere all'indirizzo e-mail: info@hivly.org oppure all'indirizzo della sede legale del Titolare, sita in Viale Lucania 35, Milano.",
        },
      ],
    },
    {
      number: "10",
      title: "TRATTAMENTO DEI DATI NEL CONTESTO DEL SISTEMA DI INTELLIGENZA ARTIFICIALE",
      blocks: [],
      subsections: [
        {
          number: "10.1",
          title: "Descrizione del sistema di Intelligenza Artificiale",
          blocks: [
            {
              kind: "p",
              text: "La piattaforma Hivly integra un sistema di Intelligenza Artificiale (IA) strutturato in componenti modulari, tra cui: un motore di abbinamento (matching) basato su algoritmo deterministico ponderato; un motore delle competenze (Skill Engine) con pipeline semantica multi-agente; un motore di valutazione per la configurazione e l'analisi di assessment strutturati; il componente di IA conversazionale in tempo reale ARIA; e un chatbot operativo basato su Retrieval-Augmented Generation (RAG). Il sistema non adotta un'architettura decisionale autonoma: ogni componente opera entro flussi di lavoro predefiniti, parametri configurabili e livelli di convalida, con supervisione umana integrata nelle fasi critiche.",
            },
          ],
        },
        {
          number: "10.2",
          title: "Dati personali trattati mediante il sistema di IA",
          blocks: [
            {
              kind: "p",
              text: "Nell'ambito del funzionamento del sistema di IA, Hivly tratta le seguenti categorie di dati personali: dati identificativi e di contatto (nome, cognome, indirizzo e-mail); dati professionali (profilo di competenze, esperienze lavorative, obiettivi di sviluppo, preferenze dichiarate); documenti caricati dall'utente (file in formato PDF, DOCX, PPTX, CSV); risposte fornite nell'ambito di valutazioni strutturate; trascrizioni delle sessioni conversazionali condotte tramite ARIA, ove applicabile; dati relativi all'utilizzo della piattaforma e alle interazioni con i componenti di IA. Gli attributi sensibili o protetti sono esclusi dai processi di valutazione algoritmica, salvo configurazione esplicita e trasparente da parte dell'utente.",
            },
          ],
        },
        {
          number: "10.3",
          title: "Finalità e base giuridica del trattamento tramite IA",
          blocks: [
            {
              kind: "p",
              text: "Il trattamento dei dati personali attraverso il sistema di IA è effettuato per le seguenti finalità, sulla base delle rispettive basi giuridiche ai sensi dell'art. 6 GDPR: (a) erogazione del servizio contrattuale (abbinamento mentori/coach, valutazione delle competenze, sessioni ARIA, generazione di report) \u2013 base giuridica: esecuzione del contratto (art. 6.1.b GDPR); (b) supporto operativo tramite chatbot RAG \u2013 base giuridica: esecuzione del contratto (art. 6.1.b GDPR) e legittimo interesse del Titolare (art. 6.1.f GDPR); (c) analisi statistica e miglioramento dei servizi tramite dati anonimizzati \u2013 base giuridica: legittimo interesse del Titolare (art. 6.1.f GDPR). Nessuna decisione che produca effetti legali o significativi sull'interessato viene adottata in modo esclusivamente automatizzato. Tutti i risultati principali rimangono soggetti a revisione, approvazione o intervento da parte di personale umano qualificato.",
            },
          ],
        },
        {
          number: "10.4",
          title: "Assenza di decisioni esclusivamente automatizzate e supervisione umana",
          blocks: [
            {
              kind: "p",
              text: "Ai sensi dell'art. 22 GDPR, Hivly garantisce che nessuna decisione produttiva di effetti giuridici o significativamente incidente sull'interessato venga adottata in modo esclusivamente automatizzato. Il sistema di IA opera secondo un modello \"human-in-the-loop\": i risultati generati (punteggi di compatibilità, profili di competenze, report di valutazione, trascrizioni ARIA) costituiscono esclusivamente strumenti di supporto decisionale e non determinazioni vincolanti. Il personale autorizzato può in ogni momento rivedere, modificare, integrare o annullare i risultati prodotti dal sistema. Le sovrascritture manuali sono tracciate nei log di sistema a fini di audit e accountability.",
            },
          ],
        },
        {
          number: "10.5",
          title: "Sub-responsabili del trattamento e trasferimento dati ai fornitori di IA",
          blocks: [
            {
              kind: "p",
              text: "I modelli linguistici di grandi dimensioni (LLM) e i servizi di embedding vettoriale impiegati dalla piattaforma sono forniti da terze parti che agiscono in qualità di sub-responsabili del trattamento, ai sensi dell'art. 28 GDPR, sulla base di appositi accordi contrattuali. I dati degli interessati sono trasmessi a tali fornitori esclusivamente in modalità di inferenza e non vengono utilizzati per addestrare modelli di IA esterni. L'intera piattaforma Hivly, inclusi i componenti di IA, è ospitata su Amazon Web Services nella regione eu-central-1 (Francoforte): tutti i dati di produzione sono conservati all'interno dell'Unione Europea. Hivly detiene la certificazione ISO/IEC 27001 per il proprio sistema di gestione della sicurezza delle informazioni.",
            },
          ],
        },
        {
          number: "10.6",
          title: "Misure contro pregiudizi ed errori del sistema di IA",
          blocks: [
            {
              kind: "p",
              text: "Hivly adotta misure tecniche e organizzative strutturate per prevenire e mitigare pregiudizi (bias) ed errori nei componenti di IA. In particolare: gli attributi sensibili o protetti sono esclusi dai processi di punteggio algoritmico; la logica di matching utilizza criteri deterministici trasparenti con pesi configurabili; le pipeline semantiche applicano soglie di confidenza, verifica della copertura e controlli di qualità; i componenti generativi operano entro vincoli di recupero (retrieval constraints) e sono soggetti a convalida umana obbligatoria prima di qualsiasi uso esterno; tutte le interazioni rilevanti sono tracciate e verificabili. Le misure di mitigazione sono soggette a monitoraggio continuo e aggiornamento periodico.",
            },
          ],
        },
        {
          number: "10.7",
          title: "Diritti dell'interessato in relazione al trattamento tramite IA",
          blocks: [
            {
              kind: "p",
              text: "Con specifico riferimento al trattamento effettuato tramite il sistema di IA, l'interessato può esercitare i diritti previsti dagli artt. 15-22 GDPR, tra cui: il diritto di accesso ai propri dati trattati; il diritto di rettifica di dati inesatti; il diritto alla cancellazione (\"diritto all'oblio\"); il diritto di limitazione del trattamento; il diritto alla portabilità dei dati; il diritto di opposizione al trattamento; il diritto di non essere sottoposto a decisioni esclusivamente automatizzate (art. 22 GDPR). Per l'esercizio di tali diritti, l'interessato può contattare il Titolare all'indirizzo e-mail info@hivly.org oppure alla sede legale indicata nella presente Policy. Nei casi previsti dalla normativa vigente, inclusa la legge europea sull'intelligenza artificiale (AI Act, Reg. UE 2024/1689), l'interessato ha inoltre diritto a ricevere spiegazioni chiare e comprensibili sul funzionamento del sistema di IA e sull'incidenza dei relativi risultati sul proprio profilo.",
            },
          ],
        },
        {
          number: "10.8",
          title: "Conformità normativa del sistema di IA",
          blocks: [
            {
              kind: "p",
              text: "Il sistema di IA di Hivly è progettato in conformità con: il Regolamento UE 2016/679 (GDPR) e i principi di privacy by design e by default; la legge europea sull'intelligenza artificiale (AI Act, Reg. UE 2024/1689) e il suo quadro basato sul rischio; i requisiti ISO/IEC 27001 e le pratiche di gestione degli incidenti ISO/IEC 27035; i requisiti organizzativi applicabili ai sensi della Direttiva NIS2. Hivly opera in qualità di titolare del trattamento per i dati degli interessati e, ove applicabile, come responsabile del trattamento per conto dei clienti aziendali. I fornitori di servizi cloud e di IA di terze parti agiscono come sub-responsabili del trattamento sulla base di accordi contrattuali adeguati ai sensi dell'art. 28 GDPR. Tutti i dati verranno in ogni caso gestiti all'interno dell'Unione Europea.",
            },
          ],
        },
      ],
    },
  ],
};

/* ── English ─────────────────────────────────── */

export const en: PolicyData = {
  title: "PRIVACY POLICY",
  subtitle: "Article 13 of EU Regulation 679/2016",
  preambleTitle: "Introduction",
  langToggle: { en: "English", it: "Italiano" },
  backLabel: "Close",
  preamble: [
    "Pursuant to European Regulation No. 679/2016 (hereinafter the \"Regulation\") and national legislation on the protection of personal data (hereinafter collectively the \"Privacy Legislation\"), Hivly S.r.l. invites you to carefully read this Privacy Policy, which contains important information regarding the protection of your Personal Data.",
    "The processing of your Personal Data will be based on the principles of fairness, lawfulness, transparency, purpose and storage limitation, data minimization, accuracy, integrity, and confidentiality, in accordance with the provisions of the Regulation.",
  ],
  sections: [
    {
      number: "1",
      title: "DATA CONTROLLER",
      blocks: [
        {
          kind: "p",
          text: "The Data Controller is Hivly S.r.l. (hereinafter \"Data Controller\"), with registered office at Viale Lucania 35, Milano.",
        },
      ],
    },
    {
      number: "2",
      title: "PERSONAL DATA PROCESSED",
      blocks: [
        {
          kind: "ul",
          items: [
            "Personal details: first and last name;",
            "Contact data: email address, mailing address, phone number;",
            "Geolocation and browsing data: IP addresses collected via technical cookies (please refer to the Cookie Policy on the website);",
            "Banking data: necessary for managing the business relationship;",
            "Multimedia data: images and video footage collected during events or promotions;",
            "Social media data: any likes, comments, and content shared on Hivly S.r.l.'s Instagram and LinkedIn pages;",
          ],
        },
      ],
    },
    {
      number: "3",
      title: "PURPOSES, LEGAL BASES, AND RETENTION",
      blocks: [
        {
          kind: "p",
          text: "The following outlines the purposes of processing, the legal bases used, and the corresponding retention periods:",
        },
        {
          kind: "table",
          cols: ["Purpose", "Legal Basis", "Retention"],
          rows: [
            [
              "Service Provision and Demos: provision of the service in accordance with the Terms and Conditions.",
              "Performance of a contract or pre-contractual measures (Art. 6.1.b GDPR).",
              "For the duration of the contractual relationship and in any case no longer than 10 years following termination.",
            ],
            [
              "Tax compliance: management of invoicing and legal obligations.",
              "Legal obligation (Art. 6.1.c GDPR).",
              "10 years as required by civil and tax law.",
            ],
            [
              "Marketing and Newsletters: sending commercial and promotional communications.",
              "Consent of the data subject (Art. 6.1.a GDPR).",
              "Until consent is withdrawn (or a maximum of 24 months).",
            ],
            [
              "Profiling: analysis of consumption habits and choices.",
              "Specific consent (Art. 6.1.a GDPR).",
              "12 months from collection, unless consent is revoked earlier.",
            ],
            [
              "Statistical analysis: improvement of services through anonymized data.",
              "Legitimate interest of the Data Controller (Art. 6.1.f GDPR).",
              "Processing in anonymous form; the data does not allow for identification.",
            ],
          ],
        },
      ],
    },
    {
      number: "4",
      title: "NATURE OF DATA PROVISION",
      blocks: [
        {
          kind: "p",
          text: "Providing data for contractual and billing purposes is necessary; failure to provide it prevents the provision of the service. For marketing and profiling purposes, providing data is optional.",
        },
        {
          kind: "p",
          text: "Whether data provision is mandatory or optional will be indicated on a case-by-case basis using symbols (e.g., \"*\") placed next to the information whose provision is mandatory to achieve the respective purpose.",
        },
      ],
    },
    {
      number: "5",
      title: "METHODS OF PROCESSING AND RECIPIENTS",
      blocks: [
        {
          kind: "p",
          text: "Data is processed using electronic and manual tools, ensuring maximum security. It may be accessible to qualified employees of Hivly S.r.l. or third parties appointed as Data Processors. Data is not disclosed to third parties unrelated to the activities of Hivly S.r.l..",
        },
        {
          kind: "p",
          text: "Personal data will be processed in accordance with the provisions of current privacy regulations, both using electronic and automated means and manually, using methods strictly related to the purposes of the processing, through databases, electronic platforms managed by Hivly S.r.l. or by third parties (appointed as Data Processors), the integrated IT systems of Hivly S.r.l. and the aforementioned third parties, and/or websites owned or used by Hivly S.r.l..",
        },
        {
          kind: "p",
          text: "Personal data will be processed in a manner that ensures maximum security and confidentiality and only by persons trained and authorized to process such data. The Data Controller adopts appropriate technical and organizational measures to ensure a level of security appropriate to the risk associated with the processing.",
        },
        {
          kind: "p",
          text: "Personal data is processed primarily at the Data Controller's premises and at the locations where the Data Processors are situated.",
        },
      ],
    },
    {
      number: "6",
      title: "DATA TRANSFER OUTSIDE THE EU",
      blocks: [
        {
          kind: "p",
          text: "Processing takes place primarily at the Data Controller's premises within the EU. In the event of a transfer to non-EU countries, the Data Controller ensures that adequate safeguards (such as Standard Contractual Clauses) are in place.",
        },
      ],
    },
    {
      number: "7",
      title: "RIGHTS OF THE DATA SUBJECT",
      blocks: [
        {
          kind: "p",
          text: "The data subject may exercise the rights provided for in Articles 15 et seq. of the GDPR:",
        },
        {
          kind: "ul",
          items: [
            "Access, rectification, erasure, and data portability.",
            "Withdrawal of consent and objection to processing.",
            "Right to lodge a complaint: You may lodge a complaint with the Italian Data Protection Authority (www.garanteprivacy.it).",
          ],
        },
      ],
    },
    {
      number: "8",
      title: "SOCIAL MEDIA",
      blocks: [
        {
          kind: "p",
          text: "In addition to the aforementioned categories of personal data, additional data provided directly by the data subject (so-called \"Contributions\") and shared on the social media pages dedicated to Hivly S.r.l. (Instagram and LinkedIn) may be processed; for information regarding the management of such data, please refer to the third parties that provide these services. Among the data collected through social media are, for example, likes, comments, images, and, in general, any content and information that the data subject may have published on the social media pages dedicated to Hivly S.r.l..",
        },
      ],
    },
    {
      number: "9",
      title: "HOW TO EXERCISE YOUR RIGHTS",
      blocks: [
        {
          kind: "p",
          text: "Should you wish to exercise the aforementioned rights, or if you would like further clarification regarding the processing of your personal data, you may write to the email address: info@hivly.org or to the address of the Data Controller's registered office, located at Viale Lucania 35, Milano.",
        },
      ],
    },
    {
      number: "10",
      title: "DATA PROCESSING IN THE CONTEXT OF THE ARTIFICIAL INTELLIGENCE SYSTEM",
      blocks: [],
      subsections: [
        {
          number: "10.1",
          title: "Description of the Artificial Intelligence System",
          blocks: [
            {
              kind: "p",
              text: "The Hivly platform integrates an Artificial Intelligence (AI) system structured into modular components, including: a matching engine based on a weighted deterministic algorithm; a Skill Engine with a multi-agent semantic pipeline; an evaluation engine for the configuration and analysis of structured assessments; the ARIA real-time conversational AI component; and an operational chatbot based on Retrieval-Augmented Generation (RAG). The system does not employ an autonomous decision-making architecture: each component operates within predefined workflows, configurable parameters, and validation levels, with human supervision integrated into critical phases.",
            },
          ],
        },
        {
          number: "10.2",
          title: "Personal Data Processed Through the AI System",
          blocks: [
            {
              kind: "p",
              text: "As part of the AI system's operation, Hivly processes the following categories of personal data: identification and contact data (first name, last name, email address); professional data (skills profile, work experience, development goals, stated preferences); documents uploaded by the user (files in PDF, DOCX, PPTX, CSV formats); answers provided as part of structured assessments; transcripts of conversational sessions conducted via ARIA, where applicable; data regarding platform usage and interactions with AI components. Sensitive or protected attributes are excluded from algorithmic evaluation processes, unless explicitly and transparently configured by the user.",
            },
          ],
        },
        {
          number: "10.3",
          title: "Purposes and Legal Basis of Processing via AI",
          blocks: [
            {
              kind: "p",
              text: "The processing of personal data through the AI system is carried out for the following purposes, based on the respective legal grounds pursuant to Art. 6 GDPR: (a) provision of the contractual service (mentor/coach matching, skills assessment, ARIA sessions, report generation) \u2013 legal basis: performance of the contract (Art. 6.1.b GDPR); (b) operational support via the RAG chatbot \u2013 legal basis: performance of the contract (Art. 6.1.b GDPR) and the Data Controller's legitimate interest (Art. 6.1.f GDPR); (c) statistical analysis and service improvement using anonymized data \u2013 legal basis: the Data Controller's legitimate interest (Art. 6.1.f GDPR). No decision that produces legal effects or significantly affects the data subject is made by automated means alone. All key results remain subject to review, approval, or intervention by qualified human personnel.",
            },
          ],
        },
        {
          number: "10.4",
          title: "Absence of Fully Automated Decisions and Human Supervision",
          blocks: [
            {
              kind: "p",
              text: "Pursuant to Art. 22 GDPR, Hivly guarantees that no decision producing legal effects or significantly affecting the data subject is made by fully automated means. The AI system operates according to a \"human-in-the-loop\" model: the results generated (compatibility scores, competency profiles, assessment reports, ARIA transcripts) are exclusively decision-support tools and not binding determinations. Authorized personnel may at any time review, modify, supplement, or cancel the results produced by the system. Manual overrides are tracked in system logs for audit and accountability purposes.",
            },
          ],
        },
        {
          number: "10.5",
          title: "Sub-processors and Data Transfer to AI Providers",
          blocks: [
            {
              kind: "p",
              text: "The large language models (LLMs) and vector embedding services used by the platform are provided by third parties acting as sub-processors, pursuant to Article 28 of the GDPR, based on specific contractual agreements. Data subjects' data is transmitted to these providers exclusively for inference purposes and is not used to train external AI models. The entire Hivly platform, including its AI components, is hosted on Amazon Web Services in the eu-central-1 (Frankfurt) region: all production data is stored within the European Union. Hivly holds ISO/IEC 27001 certification for its information security management system.",
            },
          ],
        },
        {
          number: "10.6",
          title: "Measures to Address Bias and Errors in the AI System",
          blocks: [
            {
              kind: "p",
              text: "Hivly implements structured technical and organizational measures to prevent and mitigate bias and errors in AI components. Specifically: sensitive or protected attributes are excluded from algorithmic scoring processes; the matching logic uses transparent deterministic criteria with configurable weights; semantic pipelines apply confidence thresholds, coverage verification, and quality controls; generative components operate within retrieval constraints and are subject to mandatory human validation prior to any external use; all relevant interactions are tracked and verifiable. Mitigation measures are subject to continuous monitoring and periodic updates.",
            },
          ],
        },
        {
          number: "10.7",
          title: "Data Subject Rights Regarding AI-Based Processing",
          blocks: [
            {
              kind: "p",
              text: "With specific reference to processing carried out via the AI system, the data subject may exercise the rights provided for in Articles 15\u201322 of the GDPR, including: the right of access to their processed data; the right to rectification of inaccurate data; the right to erasure (\"right to be forgotten\"); the right to restriction of processing; the right to data portability; the right to object to processing; the right not to be subject to decisions based solely on automated processing (Article 22 of the GDPR). To exercise these rights, the data subject may contact the Data Controller at the email address info@hivly.org or at the registered office indicated in this Policy. In cases provided for by applicable law, including the European Artificial Intelligence Act (AI Act, EU Reg. 2024/1689), the data subject also has the right to receive clear and understandable explanations regarding the functioning of the AI system and the impact of its results on their profile.",
            },
          ],
        },
        {
          number: "10.8",
          title: "Regulatory Compliance of the AI System",
          blocks: [
            {
              kind: "p",
              text: "Hivly's AI system is designed in compliance with: EU Regulation 2016/679 (GDPR) and the principles of privacy by design and by default; the European Artificial Intelligence Act (AI Act, EU Reg. 2024/1689) and its risk-based framework; the ISO/IEC 27001 requirements and ISO/IEC 27035 incident management practices; the applicable organizational requirements under the NIS2 Directive. Hivly acts as the data controller for the data of data subjects and, where applicable, as a data processor on behalf of corporate clients. Third-party cloud and AI service providers act as sub-processors based on appropriate contractual agreements pursuant to Article 28 of the GDPR. All data will in any case be processed within the European Union.",
            },
          ],
        },
      ],
    },
  ],
};
